/**
 * SpendingChart - Single chart with grouped bars per currency.
 * Bar heights use TRY-equivalent (via exchange rates), tooltip shows original values.
 */

import React, { useMemo } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { CurrencySpending, CURRENCY_RATES_TO_TRY } from '../../../services/documentApi';

interface SpendingChartProps {
    data: CurrencySpending[];
    loading: boolean;
}

const CURRENCY_COLORS: Record<string, string> = {
    TRY: '#3b82f6',
    EUR: '#10b981',
    CHF: '#ef4444',
    USD: '#f59e0b',
    GBP: '#8b5cf6',
};

const formatShortAmount = (value: number): string => {
    if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
    return value.toFixed(0);
};

const formatDate = (dateStr: string): string => {
    if (dateStr.length === 4) return dateStr;
    if (dateStr.length === 7) {
        const [y, m] = dateStr.split('-');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
    }
    const parts = dateStr.split('-');
    return `${parts[2]}/${parts[1]}`;
};

// Tooltip shows original values for ALL currencies present in the dataset
const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const hasValue = payload.some((e: any) => e.value > 0);
    if (!hasValue) return null;

    return (
        <div className="dashboard__chart-tooltip">
            <div className="dashboard__chart-tooltip-label">{label}</div>
            {payload.map((entry: any) => {
                const cur = entry.dataKey;
                const rate = CURRENCY_RATES_TO_TRY[cur] || 1;
                const original = entry.value / rate;
                return (
                    <div key={cur} className="dashboard__chart-tooltip-row">
                        <span className="dashboard__chart-tooltip-dot" style={{ backgroundColor: entry.color }} />
                        <span style={{ color: entry.value > 0 ? entry.color : '#9ca3af' }}>
                            {original.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {cur}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

const SpendingChart: React.FC<SpendingChartProps> = ({ data, loading }) => {
    const { chartData, currencies } = useMemo(() => {
        const dateMap: Record<string, Record<string, number>> = {};
        const currencySet = new Set<string>();

        for (const cs of data) {
            currencySet.add(cs.currency);
            const rate = CURRENCY_RATES_TO_TRY[cs.currency] || 1;
            for (const point of cs.time_series) {
                if (!dateMap[point.date]) dateMap[point.date] = {};
                // Store TRY-equivalent for bar height
                dateMap[point.date][cs.currency] = point.total * rate;
            }
        }

        const sortedDates = Object.keys(dateMap).sort();
        const allCurrencies = Array.from(currencySet);
        const merged = sortedDates.map((date) => {
            const row: Record<string, any> = { date: formatDate(date) };
            for (const cur of allCurrencies) {
                // undefined = no bar drawn; only set if actual data exists
                row[cur] = dateMap[date][cur] ?? undefined;
            }
            return row;
        });

        return { chartData: merged, currencies: allCurrencies };
    }, [data]);

    if (loading) {
        return (
            <div className="dashboard__section">
                <h3 className="dashboard__section-title">Total Spending</h3>
                <div className="dashboard__chart-placeholder">Loading...</div>
            </div>
        );
    }

    if (!chartData.length) {
        return (
            <div className="dashboard__section">
                <h3 className="dashboard__section-title">Total Spending</h3>
                <div className="dashboard__chart-placeholder">No spending data for this period</div>
            </div>
        );
    }

    return (
        <div className="dashboard__section">
            <div className="dashboard__section-header">
                <h3 className="dashboard__section-title">Total Spending</h3>
                <span className="dashboard__section-subtitle">TRY equivalent</span>
            </div>
            <div className="dashboard__chart-container">
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }} maxBarSize={32}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                        <YAxis tickFormatter={formatShortAmount} tick={{ fontSize: 12 }} stroke="#9ca3af" />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        {currencies.map((cur) => (
                            <Bar
                                key={cur}
                                dataKey={cur}
                                fill={CURRENCY_COLORS[cur] || '#6b7280'}
                                radius={[4, 4, 0, 0]}
                                minPointSize={2}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default SpendingChart;
